import React, { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import useProductsData from '@/hooks/useProductsData'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import EditButton from '@/components/EditButton'
import DeleteButton from '@/components/DeleteButton'
import useSettings from '@/hooks/useSettings'
import { getSortIndicator } from '@/lib/tableHelpers'
import ProductFormModal, {
	buildProductFormState,
	emptyProductForm
} from './components/ProductFormModal'

const getProductSortValue = (product, column) => {
	if (column === 'price') {
		const raw = product?.price?.$numberDecimal ?? product?.price
		const num = Number(raw)
		return Number.isNaN(num) ? '' : num
	}

	if (column === 'taxPercent') {
		const num = Number(product?.taxPercent)
		return Number.isNaN(num) ? '' : num
	}

	return product?.[column] ?? ''
}

const sortProducts = (products, sortColumn, sortDirection) => {
	const direction = sortDirection === 'asc' ? 1 : -1

	return [...products].sort((a, b) => {
		const firstValue = getProductSortValue(a, sortColumn)
		const secondValue = getProductSortValue(b, sortColumn)

		if (typeof firstValue === 'number' && typeof secondValue === 'number') {
			return (firstValue - secondValue) * direction
		}

		return String(firstValue).localeCompare(String(secondValue), 'pl') * direction
	})
}

const ProductsTab = () => {
	const { t } = useTranslation()
	const {
		data,
		loading,
		adding,
		updating,
		removing,
		error,
		clearError,
		add,
		update,
		remove
	} = useProductsData(); 

    const { settings } = useSettings();

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: t('notifications.errorTitle'),
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [error, clearError, t])

	const [formOpen, setFormOpen] = useState(false)
	const [formMode, setFormMode] = useState('add')
	const [formState, setFormState] = useState(emptyProductForm)
	const [editingProduct, setEditingProduct] = useState(null)
	const [sortColumn, setSortColumn] = useState('name')
	const [sortDirection, setSortDirection] = useState('asc')

	const sortedData = useMemo(
		() => sortProducts(data, sortColumn, sortDirection),
		[data, sortColumn, sortDirection]
	)

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSortColumn(column)
		setSortDirection(newDirection)
	}

	const renderSortableHeader = (column, label) => (
		<button
			type="button"
			className="flex items-center gap-1 font-semibold text-gray-700 hover:text-blue-600 transition-colors duration-200 focus:outline-none focus:text-blue-600"
			onClick={() => handleSort(column)}
		>
			{label}
			{getSortIndicator(column, sortColumn, sortDirection)}
		</button>
	)

	const openForm = (mode, product = null) => {
		setFormMode(mode)
		setEditingProduct(product)
		setFormState(buildProductFormState(product))
		setFormOpen(true)
	}

	const handleFieldChange = (field, value) => {
		setFormState(prev => ({ ...prev, [field]: value }))
	}

	const handleSubmit = async event => {
		event.preventDefault()
		if (formMode === 'add') {
			const result = await add(formState)
			if (result) {
				Swal.fire({
					title: t('notifications.successTitle'),
					text: t('settingsPage.products.messages.createdSuccess'),
					icon: 'success'
				})
				setFormOpen(false)
			}
			return
		}

		if (!editingProduct?._id) return
		const result = await update(editingProduct._id, formState)
		if (result) {
			Swal.fire({
				title: t('notifications.successTitle'),
				text: t('settingsPage.products.messages.updatedSuccess'),
				icon: 'success'
			})
			setFormOpen(false)
		}
	}

	const handleRemove = async product => {
		const result = await Swal.fire({
			title: t('notifications.confirmTitle'),
			text: t('settingsPage.products.messages.removeConfirm', { name: product.name || product._id }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('notifications.confirmDeleteButton'),
			cancelButtonText: t('notifications.cancelButton')
		})

		if (result.isConfirmed) {
			const removeResult = await remove(product._id)
			if (removeResult) {
				Swal.fire({
					title: t('notifications.successTitle'),
					text: t('settingsPage.products.messages.removedSuccess'),
					icon: 'success'
				})
			}
		}
	}

	const formatPrice = value => {
		if (value === null || value === undefined || value === '') {
			return '-'
		}
		const normalized = typeof value === 'number' ? value : Number(value)
		if (Number.isNaN(normalized)) {
			return value
		}
        let currencySymbol = settings?.companyCurrencySymbol || '';
		return `${currencySymbol} ${normalized.toFixed(2)}`
	}

	if (loading) {
		return <LoadingIndicator message={t('settingsPage.products.loading')} />
	}

	return (
		<div className="p-6">
			<div className="mb-4 flex justify-end">
				<button
					onClick={() => openForm('add')}
					className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
				>
					+ {t('settingsPage.products.actions.addProduct')}
				</button>
			</div>

			<div className="overflow-x-auto rounded-lg border border-gray-200">
				<table className="w-full text-sm text-left shadow-sm text-gray-500">
					<thead className="border-b border-gray-200 bg-gray-50">
						<tr>
							<th className="px-6 py-3">{renderSortableHeader('name', t('settingsPage.products.table.name'))}</th>
							<th className="px-6 py-3">{renderSortableHeader('sku', t('settingsPage.products.table.sku'))}</th>
							<th className="px-6 py-3">{renderSortableHeader('price', t('settingsPage.products.table.price'))}</th>
							<th className="px-6 py-3">{renderSortableHeader('taxPercent', t('settingsPage.products.table.taxPercent'))}</th>
							<th className="px-6 py-3">{renderSortableHeader('article_number', t('settingsPage.products.table.article_number'))}</th>
							<th className="px-6 py-3 text-right">{t('settingsPage.products.table.actions')}</th>
						</tr>
					</thead>
					<tbody>
						{sortedData.map(product => (
							<tr
								key={product._id ?? product.id ?? product.name}
								className="bg-white hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
							>
								<td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
									{product.name}
								</td>
								<td className="px-6 py-4">{product.sku || '-'}</td>
								<td className="px-6 py-4">{formatPrice(product.price?.$numberDecimal)}</td>
								<td className="px-6 py-4">
									{product.taxPercent != null && product.taxPercent !== '' ? product.taxPercent : '-'}
								</td>
								<td className="px-6 py-4">{product.article_number || '-'}</td>
								<td className="px-6 py-4 text-right">
									<div className="flex justify-end space-x-2">
										<EditButton
											onClick={() => openForm('edit', product)}
											disabled={updating || adding}
											isLoading={updating}
											title={t('settingsPage.products.actions.editTitle')}
											label={t('settingsPage.products.actions.edit')}
											loadingLabel={t('settingsPage.products.actions.saving')}
										/>
										<DeleteButton
											onClick={() => handleRemove(product)}
											disabled={removing}
											title={t('settingsPage.products.actions.deleteTitle')}
											label={t('settingsPage.products.actions.delete')}
										/>
									</div>
								</td>
							</tr>
						))}
						{sortedData.length === 0 && (
							<tr>
								<td colSpan={6} className="px-6 py-4 text-center text-gray-600">
									{t('settingsPage.products.empty')}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<ProductFormModal
				isOpen={formOpen}
				formMode={formMode}
				formState={formState}
				onFieldChange={handleFieldChange}
				onClose={() => setFormOpen(false)}
				onSubmit={handleSubmit}
				isSaving={formMode === 'add' ? adding : updating}
			/>
		</div>
	)
}

export default ProductsTab
